import { apiCall, ApiResult } from './core';

// Support and messaging APIs
export const supportAPI = {
  submitSupportRequest: async (requestData: Record<string, any>): Promise<ApiResult> => {
    try {
      const response = await apiCall('/support/requests', {
        method: 'POST',
        body: JSON.stringify(requestData)
      });
      return { success: true, data: response };
    } catch (error) {
      // Enhanced error handling for specific API validation errors
      const err = error as { message?: string; status?: number };
      let errorMessage = err.message || 'Failed to submit support request';

      // Check for specific API error messages (FastAPI error format)
      if (err.message && typeof err.message === 'string') {
        const lowerMessage = err.message.toLowerCase();

        if (lowerMessage.includes('only client users')) {
          errorMessage = 'Only client users can create support requests';
        } else if (lowerMessage.includes('belong to a partner') || lowerMessage.includes('no partner')) {
          errorMessage = 'Support requests can only be created by clients that belong to a partner';
        } else if (lowerMessage.includes('validation error') || lowerMessage.includes('string_too_short')) {
          errorMessage = 'Please fill in all required fields correctly';
        } else if (err.status === 403) {
          errorMessage = 'You do not have permission to perform this action';
        } else if (err.status === 401) {
          errorMessage = 'Authentication required. Please log in again.';
        }
      }

      return { success: false, error: errorMessage };
    }
  },

  getSupportRequests: async (_userRole?: string, _userId?: string): Promise<ApiResult> => {
    try {
      const response = await apiCall('/support/requests');
      return { success: true, data: response };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  getAllSupportRequests: async (clientId: string | null = null): Promise<ApiResult> => {
    try {
      let endpoint = '/support/requests/all';
      if (clientId) {
        endpoint += `?client_id=${clientId}`;
      }
      const response = await apiCall(endpoint);
      return { success: true, data: response };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  replyToSupportRequest: async (requestId: string, replyData: Record<string, any>): Promise<ApiResult> => {
    try {
      const response = await apiCall(`/support/requests/${requestId}/reply`, {
        method: 'POST',
        body: JSON.stringify(replyData)
      });
      return { success: true, data: response };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  updateSupportRequest: async (requestId: string, updateData: Record<string, any>): Promise<ApiResult> => {
    try {
      const response = await apiCall(`/support/requests/${requestId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });
      return { success: true, data: response };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  }
};

export const awsMessagingAPI = {
  getMessages: async (_userId?: string): Promise<ApiResult> => {
    return { success: true, data: [] };
  },

  sendMessage: async (_messageData: Record<string, any>): Promise<ApiResult> => {
    return { success: true, data: { id: Date.now(), status: 'sent' } };
  },

  markMessageRead: async (_messageId: string): Promise<ApiResult> => {
    return { success: true };
  },

  deleteMessage: async (_messageId: string): Promise<ApiResult> => {
    return { success: true };
  }
};

export const settingsAPI = {
  getCommunicationSettings: async (_userId?: string): Promise<ApiResult> => {
    return {
      success: true,
      data: {
        awsCommunicationEnabled: false,
        projectUpdates: true,
        technicalAlerts: true,
        serviceRecommendations: false,
        promotionalContent: false,
        emailNotifications: true,
        dashboardNotifications: true,
        smsNotifications: false
      }
    };
  },

  updateCommunicationSettings: async (_userId: string | undefined, _settings: Record<string, any>): Promise<ApiResult> => {
    return { success: true };
  }
};
